<template>
	<OverlayBackdrop v-if="display" class="modal-backdrop">
		<div
			:is="!parentTag ? 'div' : parentTag"
			:class="[
				'modal',
				isMobile ? 'modal--full-mobile' : '',
				,
				isConfirmation ? 'modal--confirmation' : '',
				,
				isDatePicker ? 'modal--date-picker' : ''
			]"
			:id="uid"
			:variant="variant"
		>
			<header class="modal__header" v-if="!isConfirmation">
				<span class="modal__title">
					{{ title }}
				</span>
				<span class="modal__close-btn" v-if="!isConfirmation">
					<Button
						:btnClick="btnClose"
						:className="'btn--icon-only'"
						:disabled="validateBeforeClose"
						:iconBeforeLabel="false"
						:iconClassName="'icon-close'"
						:id="`${uid}_close`"
					/>
				</span>
			</header>
			<body class="modal__body" ref="modalBody" id="modalBody">
				<div class="modal__body-toolbar" v-if="hasBodyToolbar">
					<slot name="bodyToolbar" />
				</div>
				<div class="modal__body-confirmation" v-if="isConfirmation">
					<div class="modal__body-title">
						{{ title }}
					</div>
					<p class="modal__body-subtitle" v-if="confirmationContent">
						<slot name="modal-content">{{
							confirmationContent
						}}</slot>
					</p>
				</div>
				<slot name="modal-content" :handleScroll="handleScroll" v-else>
				</slot>
			</body>

			<!-- The "has-scroll" class should be added to modal__footer div if the content of modal requires scrolling. 
			The class is dynamically being added/removed based on the overflow and scroll of the modal content and the viewport -->
			<footer
				class="modal__footer"
				:class="{ 'has-scroll': bodyHasScroll }"
			>
				<slot name="modal-footer" v-if="!isConfirmation">
					<ButtonGroup :items="buttons" v-if="buttons.length" />
				</slot>
				<ButtonGroup
					v-else
					:items="[
						{
							className: 'btn--primary',
							labelText: labelConfirmButton,
							btnClick: confirmBtn
						},
						{
							className: 'btn--link',
							labelText: labelCancelButton,
							btnClick: cancel
						}
					]"
				/>
			</footer>
		</div>
	</OverlayBackdrop>
</template>

<script>
import Button from '@/components/forms/button/button';
import ButtonGroup from '@/components/forms/button/button-group';
import OverlayBackdrop from '@/components/visual/overlay-backdrop';

export default {
	name: 'Modals',
	components: {
		Button,
		ButtonGroup,
		OverlayBackdrop
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		title: {
			type: String,
			default: ''
		},
		confirmationContent: {
			type: String,
			default: 'This action cannot be undone.'
		},
		labelConfirmButton: {
			type: String,
			default: 'Yes, leave this page'
		},
		labelCancelButton: {
			type: String,
			default: 'Cancel'
		},
		parentTag: {
			type: String,
			default: 'div'
		},
		validateBeforeClose: {
			type: Boolean,
			default: false
		},
		display: {
			type: Boolean,
			default: false
		},
		hasBodyToolbar: {
			type: Boolean,
			default: false
		},
		type: {
			type: String,
			default: ''
		},
		buttons: {
			type: Array,
			default: () => []
		},
		fullMobile: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			hasScroll: false,
			childHasScroll: false,
			resolvePromise: undefined,
			rejectPromise: undefined
		};
	},
	created() {
		const self = this;
		document.addEventListener('keyup', evt => {
			if (evt.key == 'Escape' && self.display) {
				self.btnClose();
			}
		});
	},
	computed: {
		isConfirmation() {
			return this.type === 'confirmation';
		},
		isMobile() {
			return this.fullMobile;
		},
		bodyHasScroll() {
			return this.hasScroll || this.childHasScroll;
		},
		isDatePicker() {
			return this.type === 'date-picker';
		}
	},
	methods: {
		btnClose(e) {
			if (e) {
				e.preventDefault();
			}
			if (!this.validateBeforeClose) {
				this.$emit('show', false);
			}
		},
		handleScroll(val) {
			this.childHasScroll = val;
		},
		confirm() {
			return new Promise((resolve, reject) => {
				this.resolvePromise = resolve;
				this.rejectPromise = reject;
			});
		},
		confirmBtn() {
			this.$emit('show', false);
			this.$emit('confirm');
			if (this.resolvePromise) {
				this.resolvePromise(true);
			}
		},
		cancel() {
			this.$emit('show', false);
			this.$emit('cancel');
			if (this.resolvePromise) {
				this.resolvePromise(false);
			}
		}
	},
	watch: {
		display: {
			immediate: true,
			handler(val) {
				if (val) {
					this.$nextTick(() => {
						const container = this.$refs.modalBody;
						const margen = 2;
						this.hasScroll =
							container.scrollHeight -
								container.offsetHeight -
								margen >
							0;
					});
				}
			}
		}
	}
};
</script>

<style lang="scss">
/**
    * [Description]
    *
    * Global Modals
    * Can use SCSS variables = TRUE
    *
    * Both modal styles and variations are included here.
    *
    * [Table of Contents]
    *
	* BASE
	* MOBILE FULLSCREEN
	* CONFIRMATION
	* SCROLL FADE
 */

/*------------------------------------*\
  #BASE
\*------------------------------------*/
@import '@/scss/5-objects/_app-elements.scss';

.modal-backdrop {
	justify-content: center;
	align-items: center;
}

.modal {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	width: 90%;
	max-height: 80vh;
	background-color: $modal-container-bkgd;
	border-radius: $modal-border-radius;
	font-family: $font-family;
	padding: $padding-md;
	z-index: 9999;
	min-width: 300px;

	@include mq($from: md) {
		width: auto;
	}

	&__header {
		display: flex;
		justify-content: space-between;
		gap: $gutter-xl;
		align-items: flex-start;
		padding-bottom: $padding-sm;
		border-bottom: 1px solid $neutral-3;
		margin-bottom: $gutter-xl;
	}

	&__title {
		@include heading('lg');
		margin: 0;
	}

	&__close-btn .btn--icon-only {
		display: flex;
		justify-content: flex-end;
	}

	&__body {
		overflow: auto;
		flex-grow: 2;
		@include scrollbar;
		min-height: 73px;
		min-width: 300px;
		padding-right: 0;
	}

	&__footer {
		text-align: right;
		margin-top: $gutter-lg;
		z-index: 1;
	}
}

/*------------------------------------*\
  #MOBILE FULLSCREEN
\*------------------------------------*/
.modal--full-mobile {
	@include mq($until: md) {
		max-width: 100%;
		width: 100%;
		height: 100vh;
		max-height: 100vh;
		border-radius: 0;

		.modal__body {
			max-height: 80vh;

			@include mq($from: lg) {
				max-height: 70vh;
			}

			.filter__fields--modal {
				@include mq($until: sm-l) {
					display: flex;
					flex-direction: column;
				}

				.dropdown {
					@include mq($until: md) {
						width: 100%;
					}
				}
			}
		}

		.modal__footer {
			@include mq($from: md) {
				bottom: 0;
				right: 0;
				position: initial;
			}
		}
	}
}

/*------------------------------------*\
  #CONFIRMATION
\*------------------------------------*/
.modal--confirmation {
	@include mq($from: md) {
		width: 400px;
	}

	.modal__body {
		overflow: visible;

		&-title {
			@include heading('lg');
			margin-bottom: $gutter-lg;
		}

		&-subtitle {
			@include body-text('regular');

			&:not(:last-child) {
				margin-bottom: $gutter-md;
			}
		}
	}
}

/*------------------------------------*\
  #SCROLL FADE
\*------------------------------------*/

.has-scroll {
	@include scroll-fade;
}

.modal__footer {
	.btn-set {
		> * {
			&:not(:last-child) {
				margin-right: $gutter-lg;
			}
			@include mq($until: lg) {
				&:not(:last-child) {
					margin-right: $gutter-lg;
				}
			}
		}

		.btn {
			&--small:nth-last-child(n + 2),
			&--small:nth-last-child(n + 2) ~ .btn--small {
				@include mq($until: lg) {
					&:not(:last-child) {
						margin-right: $gutter-lg;
					}
				}
			}
		}
	}
}

/*------------------------------------*\
  #DATE PICKER
\*------------------------------------*/

.modal--date-picker {
	max-width: 100%;
	width: 100%;
	height: 100vh;
	max-height: 100vh;
	border-radius: 0;
	padding: $padding-lg $padding-md;

	.modal__header {
		display: none;
		justify-content: space-between;
		margin-bottom: 0;
	}

	.modal__body {
		flex-grow: initial;
		height: 100%;
		max-height: 100%;
	}

	.modal__footer {
		display: none;
	}
}
</style>
